<script setup>
import 'md-editor-v3/lib/style.css';

import {
  computed,
  onMounted,
  ref,
  watchEffect,
} from 'vue';

import SideNavBar from '@/components/SideNavBar.vue';
import NotificationContainer from '@/components/NotificationContainer.vue';
import PublicFooter from '@/components/public/PublicFooter.vue';
import GenericLoader from '@/components/generic/GenericLoader.vue';
import { fetchApplication } from '@/api/common';
import i18n from '@/i18n';
import { buildPageTitle } from '@/utils/pageTitle';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  LAYOUT_CONTAINER_FULL,
  LAYOUT_CONTAINER_MEDIUM,
  LAYOUT_CONTAINER_SMALL,
  LAYOUT_PUBLIC,
} from '@/router/constants';
import { getRouterViewKey } from '@/utils/navigation';

const { commit } = useStore();
const route = useRoute();
const router = useRouter();
const store = useStore();
const showErrorPage = ref(false);
// True once the router is ready and the application has loaded; every page
// waits for it (see onMounted).
const ready = ref(false);
const user = computed(() => store.state.user);
const showSideNavCollapse = computed(() => store.state.showSideNavCollapse);
const hideSideNav = computed(() => Boolean(route.meta.hideSideNav));
const layoutContainerSize = computed(() => {
  if (route.meta.layout === LAYOUT_CONTAINER_SMALL) {
    return 'w-lg-50';
  }
  if (route.meta.layout === LAYOUT_CONTAINER_MEDIUM) {
    return 'w-lg-75';
  }
  return '';
})

const _fetchApplication = async () => {
  const response = await fetchApplication();
  if (response.status !== 200) {
    showErrorPage.value = true;
    return;
  }
  commit('setApplication', response.data);
};

const overlayClass = computed(() => {
  if (route.meta.overlay) {
    return `bg-overlay-${route.meta.overlay}`;
  }
  return 'bg-overlay-lighter';
})
const environmentClass = computed(() => route.meta.app === 'admin' ? 'environment-admin' : 'environment-default');
const routerViewKey = computed(() => getRouterViewKey(route));

watchEffect(() => {
  // A count of 1 picks the singular of a plural key ("Onderzoek | Onderzoeken"),
  // so a detail page can share the key its list uses.
  const pageTitle = route.meta.title ? i18n.global.t(route.meta.title, 1) : '';
  // De applicatienaam als basis, niet "Stramigo": ui-v1 zet de titel ook uit
  // `app.name`, en de publieke pagina's die hierheen geport zijn draaien onder
  // commercialreview.nl.
  document.title = buildPageTitle(
    pageTitle,
    store.state.app?.environment,
    store.state.app?.name,
  );
});

onMounted(async () => {
  await router.isReady();
  document.body.classList.remove('g-sidenav-hidden');
  document.body.classList.remove('g-sidenav-show');
  commit('setShowSideNavCollapse', store.state.showSideNavCollapse);
  // Pages read the application (logo, name, terms) while they render, so no
  // layout shows before it has loaded; when loading fails, the error page
  // shows instead.
  await _fetchApplication();
  ready.value = true;
});
</script>

<template>
  <div
    class="app-container"
    :class="[overlayClass, environmentClass]"
  >
    <div
      v-if="showErrorPage"
      class="vh-100 d-flex align-items-center justify-content-center"
    >
      <div class="alert alert-warning text-center">
        <i
          class="fa fa-exclamation-triangle fa-3x"
        ></i>
        <hr/>
        <h5 class="text-center m-0">
          {{ $t('sentence.application_unavailable') }}
        </h5>
      </div>
    </div>
    <div
      v-else-if="!ready"
      class="vh-100 d-flex align-items-center justify-content-center"
      data-testid="app-loading"
    >
      <generic-loader/>
    </div>
    <div v-else-if="$route.meta.layout === 'empty'">
      <router-view/>
    </div>
    <!--
      De reviewersomgeving: dezelfde sidebar als de bedrijfsomgeving, met de
      knoppen voor de respondent (zie SideNavBar). Ook uitgelogd, want een deel
      van deze pagina's is publiek. De pagina's zetten zelf GenericPageHeader,
      met de breadcrumbs; de footer uit ui-v1 blijft.
    -->
    <div v-else-if="route.meta.layout === LAYOUT_PUBLIC">
      <side-nav-bar/>
      <main
        class="main-content public-layout"
        :class="{ 'main-content-collapsed': showSideNavCollapse }"
      >
        <div class="public-content container-fluid pt-2 pb-4">
          <router-view :key="route.fullPath"/>
        </div>
        <public-footer/>
      </main>
    </div>
    <div v-else-if="[LAYOUT_CONTAINER_SMALL, LAYOUT_CONTAINER_MEDIUM].includes(route.meta.layout)">
      <div class="min-vh-100 container d-flex justify-content-center pt-5 pb-5">
        <div
          class="row w-100 d-flex align-items-center"
          :class="layoutContainerSize"
        >
          <router-view/>
        </div>
      </div>
    </div>
    <div
      v-else-if="$route.meta.layout === LAYOUT_CONTAINER_FULL"
      class="container flex-grow-1 d-flex flex-column"
    >
      <router-view class="flex-grow-1 d-flex flex-column"/>
    </div>
    <div v-else>
      <side-nav-bar
        v-if="user.isAuthenticated && !hideSideNav"
      />
      <main
        class="main-content"
        :class="{
          'main-content-collapsed': showSideNavCollapse && !hideSideNav,
          'main-content-no-sidenav': hideSideNav,
        }"
      >

        <div class="container-fluid pt-2 pb-4">
          <router-view :key="routerViewKey"/>
        </div>
      </main>
    </div>
  </div>

  <notification-container/>
</template>

<style scoped>
@import "./assets/css/style.css";

.app-container {
  transition: background-color ease-in-out 0.2s;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

.public-layout {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 100vh;
}

.public-content {
  flex-grow: 1;
}

.environment-default {
  /* Transparent so the body background shows through unchanged. A white wash
     here would flatten the website's warm tone to near-white. */
  --overlay-lighter: rgba(255, 255, 255, 0);
  --overlay-light: rgba(255, 255, 255, 0.5);
}

.environment-admin {
  --overlay-lighter: rgba(238, 245, 255, 0.68);
  --overlay-light: rgba(232, 241, 255, 0.46);
}

.bg-overlay-lighter {
  background-color: var(--overlay-lighter);
}

.bg-overlay-light {
  background-color: var(--overlay-light);
}

.bg-overlay-none {
  background-color: rgba(255, 255, 255, 0.0);
}

.bg-overlay-dark {
  background-color: rgba(0, 0, 0, 0.5);
}

.bg-overlay-darker {
  background-color: rgba(0, 0, 0, 0.8);
}

@media (min-width: 1200px) {
  .main-content {
    margin-left: 15rem !important;
    transition: margin-left .2s ease-in-out;
    width: calc(100% - 15rem);
  }

  .main-content.main-content-collapsed {
    margin-left: 4.25rem !important;
    width: calc(100% - 4.25rem);
  }

  .main-content.main-content-no-sidenav {
    margin-left: 0 !important;
    width: 100%;
  }
}
</style>
